/**
 * NotificationsBell — topbar bell with unread badge + dropdown panel.
 * Clicking a notification marks it read and (when it has a task) opens
 * that task. Live: new rows arrive over realtime via useNotifications.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { useNotifications } from '../../hooks/useNotifications.js';
import { useAuth } from '../../context/AuthContext.jsx';
import { getTaskRoute } from '../../utils/roleUtils.js';
import Icon from '../ui/Icons.jsx';
import Spinner from '../ui/Spinner.jsx';

const TYPE_ICON = {
  task_assigned: 'sheet',
  comment_added: 'send',
  doubt_raised: 'doubts',
  doubt_answered: 'doubts',
  task_done: 'done',
};

function timeAgo(iso) {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return '';
  }
}

export default function NotificationsBell() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const { items, unreadCount, loading, markRead, markAllRead } = useNotifications();

  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleItemClick = (n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    if (n.task_id && profile?.role) {
      navigate(getTaskRoute(profile.role, n.task_id));
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative w-[34px] h-[34px] rounded-md border grid place-items-center hover:bg-slate-50 transition-colors flex-shrink-0"
        style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
        title="Notifications"
        aria-label={unreadCount ? `Notifications (${unreadCount} unread)` : 'Notifications'}
        aria-expanded={open}
        aria-haspopup="true"
      >
        <Icon name="bell" size={16} className="text-ink" />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[17px] h-[17px] px-1 rounded-full text-[10px] font-bold text-white grid place-items-center border-2"
            style={{ background: '#DC2626', borderColor: 'var(--card)' }}
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="orin-pop absolute right-0 top-full mt-1.5 z-50 w-[340px] bg-card border border-border-base rounded-lg shadow-xl overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border-base">
            <span className="text-sm font-semibold text-ink">Notifications</span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="text-xs font-medium hover:underline"
                style={{ color: 'var(--accent)' }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[380px] overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8"><Spinner size={18} /></div>
            ) : items.length === 0 ? (
              <p className="text-xs text-muted text-center py-8 px-4">
                Nothing yet. You&apos;ll see task assignments, comments and
                doubt replies here.
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleItemClick(n)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-3 border-b border-border-base last:border-b-0 transition-colors hover:bg-canvas ${
                    n.read ? '' : 'bg-[var(--accent-50)]'
                  }`}
                >
                  <span
                    className="mt-0.5 w-7 h-7 rounded-full grid place-items-center flex-shrink-0"
                    style={{ background: 'var(--surface-muted)' }}
                  >
                    <Icon name={TYPE_ICON[n.type] || 'bell'} size={13} className="text-muted" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className={`block text-[13px] leading-snug ${n.read ? 'text-muted' : 'text-ink font-medium'}`}>
                      {n.message}
                    </span>
                    <span className="block text-[11px] text-muted mt-0.5">{timeAgo(n.created_at)}</span>
                  </span>
                  {!n.read && (
                    <span
                      className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                      style={{ background: 'var(--accent)' }}
                      aria-hidden="true"
                    />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
